/**
 * Conversation Flow Reducer
 *
 * Manages the frontend conversational mode using a discriminated union.
 * The backend has mutually exclusive execution paths modeled as `ActiveFlow`:
 *
 *   ANSWERING        – normal Q&A
 *   FORM_COLLECTING   – bot is asking questions to collect visitor info (suggested_answers)
 *   REDIRECTING       – about to redirect
 *   BOOKING           – collecting email before CTA redirect
 *
 * Separate from ActiveFlow, `isFormAssistant` is a widget config flag:
 * the widget is displayed in copilot mode on a CTA page to help the visitor
 * fill the page's form. Booking is blocked in this mode (user is already
 * on the target page).
 */

import type { VisitorProfile } from '../../services/inboundx';

// ---------------------------------------------------------------------------
// Active Flow — discriminated union (mutually exclusive modes)
// ---------------------------------------------------------------------------

export type ActiveFlow =
    | { type: 'ANSWERING' }
    | { type: 'FORM_COLLECTING' }
    | { type: 'REDIRECTING'; redirectUrl: string | null }
    | {
          type: 'BOOKING';
          isAwaitingEmail: boolean;
          pendingRedirectUrl: string | null;
          pendingCtaId: string | null;
          pendingBookingMessage: string | null;
          /** CTA action mode — 'popup' opens provider SDK overlay instead of navigating */
          ctaAction: 'navigate' | 'popup' | 'in-chat';
          /** True once the backend has responded with booking_state.is_booking_flow = true */
          bookingResponseReceived: boolean;
          /**
           * First entry of backend's `pending_booking_fields` — the field the
           * chatbot is currently asking for (e.g. 'email', 'team_size'). Null
           * before the first backend response arrives. Drives the reminder pill.
           */
          pendingFieldId: string | null;
      };

// ---------------------------------------------------------------------------
// State
// ---------------------------------------------------------------------------

export interface ConversationFlowState {
    activeFlow: ActiveFlow;
    visitorProfile: VisitorProfile | null;
    /** Widget is in copilot mode on a CTA page helping visitor fill a form. Blocks booking. */
    isFormAssistant: boolean;
    /** URL of the calendar embed currently shown in-chat (null when not showing) */
    inChatEmbedUrl: string | null;
    /** Layout the widget was in before the embed opened, so we can restore it */
    layoutBeforeEmbed: string | null;
    /** Where to navigate the visitor after the embedded calendar reports a successful booking. Null when the active CTA has no post-booking redirect configured. */
    inChatEmbedPostBookingRedirectUrl: string | null;
}

// ---------------------------------------------------------------------------
// Actions
// ---------------------------------------------------------------------------

export type ConversationFlowAction =
    | { type: 'ENTER_ANSWERING' }
    | { type: 'ENTER_FORM_COLLECTING' }
    | { type: 'ENTER_REDIRECTING'; payload: { redirectUrl: string | null } }
    | {
          type: 'START_BOOKING';
          payload: {
              ctaId: string;
              redirectUrl: string;
              bookingMessage: string;
              ctaAction?: 'navigate' | 'popup' | 'in-chat';
          };
      }
    | { type: 'CONFIRM_BOOKING_RESPONSE' }
    | { type: 'UPDATE_BOOKING_REDIRECT_URL'; payload: { redirectUrl: string } }
    | { type: 'UPDATE_PENDING_BOOKING_FIELDS'; payload: { fields: string[] } }
    | { type: 'COMPLETE_BOOKING' }
    | { type: 'CANCEL_BOOKING' }
    | { type: 'UPDATE_VISITOR_PROFILE'; payload: { visitorProfile: VisitorProfile | null } }
    | {
          type: 'SHOW_IN_CHAT_EMBED';
          payload: {
              url: string;
              layoutBeforeEmbed?: string;
              postBookingRedirectUrl?: string;
          };
      }
    | { type: 'HIDE_IN_CHAT_EMBED' }
    | { type: 'RESET' };

// ---------------------------------------------------------------------------
// Initial state
// ---------------------------------------------------------------------------

export interface ConversationFlowInit {
    isFormAssistant?: boolean;
}

export function getInitialConversationFlowState(
    init?: ConversationFlowInit
): ConversationFlowState {
    return {
        activeFlow: { type: 'ANSWERING' },
        visitorProfile: null,
        isFormAssistant: init?.isFormAssistant ?? false,
        inChatEmbedUrl: null,
        layoutBeforeEmbed: null,
        inChatEmbedPostBookingRedirectUrl: null,
    };
}

// ---------------------------------------------------------------------------
// Reducer
// ---------------------------------------------------------------------------

export function conversationFlowReducer(
    state: ConversationFlowState,
    action: ConversationFlowAction
): ConversationFlowState {
    switch (action.type) {
        case 'ENTER_ANSWERING':
            return {
                ...state,
                activeFlow: { type: 'ANSWERING' },
            };

        case 'ENTER_FORM_COLLECTING':
            return {
                ...state,
                activeFlow: { type: 'FORM_COLLECTING' },
            };

        case 'ENTER_REDIRECTING':
            return {
                ...state,
                activeFlow: {
                    type: 'REDIRECTING',
                    redirectUrl: action.payload.redirectUrl,
                },
            };

        case 'START_BOOKING':
            // Booking blocked during form collection or in form assistant mode
            if (state.isFormAssistant || state.activeFlow.type === 'FORM_COLLECTING') {
                return state;
            }
            return {
                ...state,
                activeFlow: {
                    type: 'BOOKING',
                    isAwaitingEmail: true,
                    pendingRedirectUrl: action.payload.redirectUrl,
                    pendingCtaId: action.payload.ctaId,
                    pendingBookingMessage: action.payload.bookingMessage,
                    ctaAction: action.payload.ctaAction ?? 'navigate',
                    bookingResponseReceived: false,
                    pendingFieldId: null,
                },
            };

        case 'CONFIRM_BOOKING_RESPONSE':
            if (state.activeFlow.type !== 'BOOKING') {
                return state;
            }
            return {
                ...state,
                activeFlow: {
                    ...state.activeFlow,
                    bookingResponseReceived: true,
                },
            };

        case 'UPDATE_BOOKING_REDIRECT_URL':
            if (state.activeFlow.type !== 'BOOKING') {
                return state;
            }
            return {
                ...state,
                activeFlow: {
                    ...state.activeFlow,
                    pendingRedirectUrl: action.payload.redirectUrl,
                },
            };

        case 'UPDATE_PENDING_BOOKING_FIELDS':
            if (state.activeFlow.type !== 'BOOKING') {
                return state;
            }
            return {
                ...state,
                activeFlow: {
                    ...state.activeFlow,
                    pendingFieldId: action.payload.fields[0] ?? null,
                },
            };

        case 'COMPLETE_BOOKING':
        case 'CANCEL_BOOKING':
            return {
                ...state,
                activeFlow: { type: 'ANSWERING' },
            };

        case 'UPDATE_VISITOR_PROFILE':
            return {
                ...state,
                visitorProfile: action.payload.visitorProfile,
            };

        case 'SHOW_IN_CHAT_EMBED':
            return {
                ...state,
                inChatEmbedUrl: action.payload.url,
                layoutBeforeEmbed: action.payload.layoutBeforeEmbed ?? null,
                inChatEmbedPostBookingRedirectUrl: action.payload.postBookingRedirectUrl ?? null,
            };

        case 'HIDE_IN_CHAT_EMBED':
            return {
                ...state,
                inChatEmbedUrl: null,
                layoutBeforeEmbed: null,
                inChatEmbedPostBookingRedirectUrl: null,
            };

        case 'RESET':
            return getInitialConversationFlowState({ isFormAssistant: state.isFormAssistant });

        default:
            return state;
    }
}
